/**
 * AuthContext — Google OAuth + EQUIPO lookup
 *
 * Flujo:
 *  1. signInWithGoogle()        → Firebase popup
 *  2. onAuthStateChanged()      → usuario persistido entre recargas
 *  3. Verificar @thecrewagencia.com  → si no, signOut() + error
 *  4. Buscar email en GET /api/equipo → obtener rol, nombre, id
 *  5. Exponer { user, miembro, rol, loading, error, signIn, signOut }
 */
import { limpiarOffline } from "../lib/offlineCache.js";
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, googleProvider } from "../lib/firebase.js";
import { api } from "../api/client.js";
import { setSentryUser, clearSentryUser } from "../lib/sentry.js";

const AuthContext = createContext(null);

const ALLOWED_DOMAIN = "thecrewagencia.com";

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);   // Firebase user object
  const [miembro, setMiembro] = useState(null);   // Row from EQUIPO sheet
  const [rol,     setRol]     = useState(null);   // String: "Director Creativo", etc.
  const [loading, setLoading] = useState(true);   // Checking persisted session
  const [error,   setError]   = useState(null);   // Auth error string

  /* ── Lookup user in EQUIPO sheet ─────────────────────────────────────── */
  const lookupEquipo = useCallback(async (email) => {
    try {
      const res  = await api.getEquipo();
      const list = res.equipo ?? res ?? [];
      const found = list.find(
        m => m.email?.toLowerCase() === email.toLowerCase()
      );
      const hoyPY = new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
      if (found && found.tipo_cuenta === "evento" && found.vigente_hasta && String(found.vigente_hasta).slice(0, 10) < hoyPY) {
        await fbSignOut(auth);
        setUser(null); setMiembro(null); setRol(null);
        setError(`Tu acceso por evento venció el ${String(found.vigente_hasta).slice(0, 10)}. Pedile a la coordinación que lo extienda.`);
      } else if (found && (found.activo === false || found.activo === "FALSE")) {
        await fbSignOut(auth);
        setUser(null); setMiembro(null); setRol(null);
        setError(`Tu cuenta (${email}) está inactiva. Pedile a dirección que la reactive.`);
      } else if (found) {
        setMiembro(found);
        setRol(found.rol ?? "Equipo");
      } else {
        // No está en la hoja EQUIPO → acceso denegado
        await fbSignOut(auth);
        setUser(null);
        setMiembro(null);
        setRol(null);
        setError(
          `Tu cuenta (${email}) no está registrada en el equipo. ` +
          `Pedile a un admin que te agregue en la hoja EQUIPO.`
        );
      }
    } catch (e) {
      // Si el backend está caído, no bloquear — dejar pasar con rol "Equipo"
      console.warn("[AuthContext] No se pudo cargar EQUIPO:", e.message);
      setMiembro({ email, nombre: email.split("@")[0], rol: "Equipo" });
      setRol("Equipo");
    }
  }, []);

  // Al subir o borrar la foto del perfil se relee el miembro para que todos los avatares se actualicen al instante.
  useEffect(() => {
    const al = () => { if (user?.email) lookupEquipo(user.email); };
    window.addEventListener("crew-foto-actualizada", al);
    return () => window.removeEventListener("crew-foto-actualizada", al);
  }, [user?.email, lookupEquipo]);

  /* ── Listen to Firebase auth state ──────────────────────────────────── */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setError(null);

      if (!fbUser) {
        setUser(null);
        setMiembro(null);
        setRol(null);
        clearSentryUser();
        setLoading(false);
        return;
      }

      // Antes acá se exigía @thecrewagencia.com. Ahora la puerta es EQUIPO (el servidor y lookupEquipo lo verifican): entran las
      // cuentas de la agencia y también las de usuarios por evento (Google personal o correo y contraseña) mientras estén vigentes.
      const email = fbUser.email ?? "";

      setUser(fbUser);
      setSentryUser(fbUser);
      await lookupEquipo(email);
      setLoading(false);
    });

    return unsub;
  }, [lookupEquipo]);

  /* ── Actions ─────────────────────────────────────────────────────────── */
  const signIn = useCallback(async () => {
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
      // onAuthStateChanged handles the rest
    } catch (e) {
      if (e.code !== "auth/popup-closed-by-user") {
        setError(e.message);
      }
    }
  }, []);

  // Usuarios por evento que no usan Google: correo y contraseña (requiere habilitar ese método en Firebase Authentication).
  const signInConPassword = useCallback(async (email, password) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (e) {
      const m = {
        "auth/invalid-credential": "El correo o la contraseña no son correctos.", "auth/wrong-password": "El correo o la contraseña no son correctos.",
        "auth/user-not-found": "El correo o la contraseña no son correctos.", "auth/user-disabled": "Tu acceso está desactivado. Pedile a la coordinación que lo reactive.",
        "auth/operation-not-allowed": "El ingreso con contraseña todavía no está habilitado. Entrá con Google.", "auth/too-many-requests": "Demasiados intentos. Probá de nuevo en unos minutos.",
      }[e.code] || e.message;
      setError(m);
    }
  }, []);

  const signOut = useCallback(async () => {
    limpiarOffline();   // lo guardado para leer sin conexión es de esta persona: se borra al salir
    await fbSignOut(auth);
    setUser(null);
    setMiembro(null);
    setRol(null);
  }, []);

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  // initials para avatar: "Malte Bremer" → "MB"
  const displayName = miembro?.nombre ?? user?.displayName ?? "?";
  const initials = displayName
    .split(" ")
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? "")
    .join("") || "?";
  // La foto que la persona subió a su perfil manda sobre la de su cuenta de Google.
  const photoURL = miembro?.foto_url || user?.photoURL || null;
  const refrescarMiembro = useCallback(async () => { if (user?.email) await lookupEquipo(user.email); }, [user?.email, lookupEquipo]);

  /* ── Role matcher — memoizado: rol cambia raramente, no recalcular en cada render
   * Espejo multi-rol del backend: considera `rol` + `titulo` + `roles_extra` (todos opcionales).
   * Asi el CCC con titulo "CCC - Coordinador (Diseñador Gráfico)" sigue siendo CCC.
   */
  const roles = useMemo(() => {
    // String concatenado de TODAS las fuentes de rol del usuario, en minúsculas
    const partes = [];
    if (rol) partes.push(String(rol));
    if (miembro?.titulo) partes.push(String(miembro.titulo));
    // roles_extra puede venir como array nativo o string JSON (pg-adapter legacy stringifica arrays)
    let extras = miembro?.roles_extra;
    if (typeof extras === "string") { try { extras = JSON.parse(extras); } catch { extras = []; } }
    if (Array.isArray(extras)) partes.push(...extras.map(String));
    const r = partes.join(" ").toLowerCase();

    // Tokens limpios (para checks exactos como "ec" o "com")
    const tokens = new Set(
      r.split(/[\s\-\/,;|·.()&+]+/).filter(Boolean)
    );
    const has = (...kw) => kw.some(k => r.includes(k));
    const hasTok = (...tk) => tk.some(t => tokens.has(t));

    // Los roles son ADITIVOS: una persona puede ser CCC Y Diseñador al mismo tiempo
    // (ej. Mathi: rol="CCC" + titulo="(Diseñador Gráfico)"). Cada flag refleja un rol,
    // los permisos abajo se calculan tomando la UNIÓN de lo que cada rol habilita.
    // PRIORIDAD: tokens exactos (hasTok) primero, después frases largas (has).
    // Antes usábamos has("coo") que matcheaba "COOrdinador" → falso positivo.
    // v8.6.22 · Fase 7 — nuevo rol MD (Managing Director / Director General),
    // mismo nivel que CEO → se pliega directo en isAdmin (todos los permisos
    // de abajo que ya dependen de isAdmin se extienden solos, sin tocarlos).
    const isAdmin        = hasTok("admin", "ceo", "md") || has("director creativo", "director general", "fundador", "founder");
    const isMD           = hasTok("md") || has("managing director");
    const isCOO          = hasTok("coo") || has("director de operaciones", "directora de operaciones", "arquitecta de procesos", "arquitecto de procesos");
    // v8.6.22 · Fase 7 — CCC absorbido por COO (COO pasa a ser 100% operativo).
    // isCCC se deja (no rompe nada dejarlo) pero ya no lo activa ninguna persona real.
    const isCCC          = hasTok("ccc") || has("control de calidad", "centro de control");
    // Cockpits por rol (12-oct) · CPC = coordinación de producciones y coberturas (René). Antes no tenía bandera propia.
    const isCPC          = hasTok("cpc") || has("coordinador de producciones", "coordinadora de producciones");
    // El rol PRINCIPAL (campo `rol`) decide dónde aterriza; los roles extra suman permisos pero no mueven la entrada.
    const rolBase        = String(rol || "").trim().toLowerCase();
    const isCFO          = hasTok("cfo") || has("director financiero", "directora financiera", "gerente financiera", "gerente administrativo");
    const isCuenta       = hasTok("ec") || has("ejecutiv", "brand manager", "estratega de crecimiento");
    const isCM           = hasTok("com", "smm") || has("content manager", "community manager", "social media");
    const isEspecialista = hasTok("diseñador", "disenador", "audiovisual", "editor", "fotógrafo", "fotografo");
    const isIT           = hasTok("it", "ti") || has("it lead");   // TI: salud del sistema, accesos y soporte (no finanzas ni cargos)
    const isHunter       = hasTok("hunter") || has("gestor comercial", "ventas comercial");
    const isFarmer       = hasTok("farmer") || has("análisis", "analisis data");

    let defaultPath = "/inicio";
    // v8.6.22 · Fase 7 — COO absorbió el trabajo operativo diario de CCC
    // (asignación, apelaciones); landing pasa a ser la herramienta
    // operativa (inbox), no el cockpit ejecutivo.
    // 12-oct · MD entra por la pantalla ejecutiva (antes caía en la bandeja de EC por sus roles extra).
    // CPC entra por Coberturas · control; COM/SMM por su cola de publicación y QC.
    if (isMD) defaultPath = "/inicio";
    else if (isCOO || isCCC) defaultPath = "/ccc/inbox";
    else if (isCPC) defaultPath = "/coberturas/control";
    else if (rolBase === "com" || rolBase === "smm") defaultPath = "/com/cockpit";
    else if (isCuenta) defaultPath = "/ec/inbox";
    else if (isCFO)    defaultPath = "/finanzas";
    else if (isHunter) defaultPath = "/leads";
    else if (isIT)     defaultPath = "/it-lead";

    // Permisos ADITIVOS: si CUALQUIER rol del usuario los habilita, los tiene.
    // Mathi (CCC + Diseñador) → CCC habilita ambos permisos, independiente de ser también especialista.
    const puedeEditarFechas       = isAdmin || isCOO || isCCC || isCuenta || isCM;
    const puedeAsignarResponsable = isAdmin || isCOO || isCCC;
    // Gestión de grupos (renombrar/archivar/eliminar/reordenar): CEO/COO/CCC/EC
    const puedeGestionarGrupos    = isAdmin || isCOO || isCCC || isCuenta;
    // Admin: ver/editar datos sensibles (RUC, sueldos, contratos, agencia)
    // Decisión CEO (v5.2): solo CEO + COO + CFO
    const puedeVerAdmin           = isAdmin || isCOO || isCFO;
    // Perfiles de autonomía del Autopiloto: SOLO CEO, MD y COO (mismo criterio exacto que el backend; no entran admin genérico ni CFO).
    const puedePerfiles           = hasTok("ceo", "md", "coo");
    // v8.4.30 — Crear clientes nuevos: CEO/COO/CFO + CCC (pedido explícito del
    // equipo). Deliberadamente separado de puedeVerAdmin: CCC puede dar de alta
    // un cliente pero sigue sin ver RUC/sueldos/contratos/datos fiscales.
    const puedeCrearClientes      = isAdmin || isCOO || isCFO || isCCC;
    // v6.4.1 — Ver equipo (score, ranking, desempeño global): admin + CCC
    const puedeVerEquipo          = isAdmin || isCOO || isCFO || isCCC;
    // v7.0.1 — Generar presentaciones de campaña: solo EC y COM (CEO/COO override)
    const puedeGenerarPresentaciones = isAdmin || isCOO || isCuenta || isCM;

    // Se expone el estado de permisos en window.__crewAuth (soporte); ya no se imprime en consola.
    if (typeof window !== "undefined") {
      // Exponer en window para debug desde consola
      window.__crewAuth = {
        rol, titulo: miembro?.titulo, roles_extra: miembro?.roles_extra,
        flags: { isAdmin, isMD, isCOO, isCCC, isCPC, isCFO, isCuenta, isCM, isEspecialista },
        permisos: { puedeEditarFechas, puedeAsignarResponsable },
        buildId: "v2026-06-DEBUG",
      };
    }

    return { isAdmin, isMD, isCOO, isCCC, isCPC, rolBase, isCFO, isIT, isCuenta, isCM, isEspecialista, isHunter, isFarmer, puedeEditarFechas, puedeAsignarResponsable, puedeGestionarGrupos, puedeVerAdmin, puedeCrearClientes, puedeVerEquipo, puedeGenerarPresentaciones, puedePerfiles, defaultPath };
  }, [rol, miembro?.titulo, miembro?.roles_extra]);

  const { isAdmin, isMD, isCOO, isCCC, isCPC, rolBase, isCFO, isIT, isCuenta, isCM, isEspecialista, isHunter, isFarmer, puedeEditarFechas, puedeAsignarResponsable, puedeGestionarGrupos, puedeVerAdmin, puedeCrearClientes, puedeVerEquipo, puedeGenerarPresentaciones, puedePerfiles, defaultPath } = roles;

  const value = {
    user,
    miembro,
    rol,
    loading,
    error,
    signIn,
    signInConPassword,
    signOut,
    displayName,
    initials,
    photoURL,
    refrescarMiembro,
    // Role booleans (soft — todos pueden ver todo, esto controla defaults)
    isAdmin,
    isMD,
    isCOO,
    isCCC,
    isCPC,
    rolBase,
    isCFO,
    isIT,
    isCuenta,
    isCM,
    isEspecialista,
    puedeEditarFechas,
    puedeAsignarResponsable,
    puedeGestionarGrupos,
    puedeVerAdmin,
    puedePerfiles,
    puedeCrearClientes,
    puedeVerEquipo,
    puedeGenerarPresentaciones,
    isHunter,
    isFarmer,
    defaultPath,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
